import React, { useState } from 'react';
import { connect } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import { IParticipantForUser, IPartyForUser, StreamType, TUserId } from '../../PartyClientResponse';
import StreamPlayer from '../../controller/video/StreamPlayer';
import { AppMapProps, IAppState } from '../../redux/reducer';
import { useAppState } from '../../state/AppState';
import { IAreaChannelAllowed } from './AppConstants';
import { highlightParticipant } from '../../controller/client/PartyClient';
import { getIdToken } from '../../controller/PartyDB';
import { Button } from 'antd';
import SelfVideoBubble from '../video/SelfVideoBubble';

const STEP_DOWN_AS_MAIN = gql`
  mutation StepDownAsMain($partyKey: String!, $participantId: String) {
    updateMainParticipantStream(input: { partyKey: $partyKey, participantId: $participantId }) {
      success
    }
  }
`;

const ParticipantThumbnail = ({
  participant,
  width,
  height,
  square,
}: {
  participant: IParticipantForUser;
  width: number;
  height: number;
  square?: boolean;
}) => {
  return (
    <div
      className={`thumbnail ${square ? 'square' : ''}`}
      style={{
        position: 'absolute',
        backgroundImage: `url('${participant.photoURL}')`,
        width,
        height,
      }}></div>
  );
};

export const UnspotlightButton = ({ participant }: { participant: IParticipantForUser }) => {
  const { user, party, isHost, isMod } = useAppState();
  const [loading, setLoading] = useState<boolean>(false);
  const [stepDown, { loading: stepDownLoading }] = useMutation(STEP_DOWN_AS_MAIN);
  const canUnspotlight = isHost || isMod || user?.uid === participant.uid;

  if (!party) {
    return <div />;
  }
  const isSpotlighted = party.stage.spotlight?.participant.uid === participant.uid;
  const isMainVideo =
    party.stage.stream.type === StreamType.Participant &&
    party.stage.stream.mainParticipantId === participant.uid;

  if (isMainVideo && canUnspotlight) {
    let text = isHost || isMod ? 'unfocus' : 'step down';
    return (
      <div className='unspotlight-button-container'>
        <Button
          style={{ transform: 'translateY(-100%)' }}
          loading={stepDownLoading}
          onClick={async () => {
            console.log('CALLING');
            try {
              await stepDown({
                variables: {
                  partyKey: party.key,
                  participantId: null,
                },
              });
            } catch (err) {
              console.log(err);
            }
          }}
          className='unspotlight-button'>
          {text}
        </Button>
      </div>
    );
  }

  if (isSpotlighted && canUnspotlight) {
    let text = isHost || isMod ? 'unspotlight' : 'step down';
    return (
      <div className='unspotlight-button-container'>
        <Button
          loading={loading}
          onClick={async () => {
            setLoading(true);
            try {
              await highlightParticipant({
                idToken: await getIdToken(),
                key: party.key,
                participant: null,
              });
            } catch (err) {
              console.log(err);
            }
            setLoading(false);
          }}
          className='unspotlight-button'>
          {text}
        </Button>
      </div>
    );
  }

  return <div />;
};

const ParticipantBubble = ({
  participantId,
  audio,
  stream,
  left,
  top,
  radius,
  party,
  style = {},
  square,
}: {
  participantId: TUserId;
  party: IPartyForUser;
  audio: IAreaChannelAllowed;
  stream?: MediaStream;
  userIsHost?: boolean;
  left: number;
  top: number;
  radius: number;
  style?: React.CSSProperties;
  square?: boolean;
} & IAppState) => {
  const { user, allParticipants, isHost, isMod } = useAppState();

  if (participantId === user?.uid) {
    return <SelfVideoBubble style={style} />;
  }

  const participant = allParticipants.find((p) => p.uid === participantId);

  if (!participant) {
    return <div />;
  }

  const isHighlighted = party.stage.spotlight?.participant.uid === participant.uid;
  const canSpotlight = isHost || isMod;
  const audioEnabled =
    audio !== 'muted' &&
    !party.stage.muteAllParticipants &&
    !(participant.tags || []).includes('audio-muted');

  const width = radius * 2;
  const height = radius * 2;

  return (
    <>
      <div
        key={`${participant.uid}-bubble`}
        className={`participant-bubble ${square ? 'square' : ''}`}
        style={{
          top,
          left,
          position: 'absolute',
          width,
          height,
          ...style,
          // boxShadow: `0px 0px ${Math.round(volumeMeter * 40)}px white`,
        }}>
        <ParticipantThumbnail
          width={radius * 2}
          height={radius * 2}
          participant={participant}
          square
        />
        <StreamPlayer
          height={height}
          key={`${participant.uid}-StreamPlayer`}
          width={width}
          participant={participant}
          shouldSubscribe
          square={square}
          audioEnabled={audioEnabled}></StreamPlayer>
        <div key={`${participant.uid}-name`} className='participant-name'>
          {participant.displayName}
        </div>
        <div>
          <UnspotlightButton participant={participant} />
        </div>
        <p className='centered-text' style={{ display: 'none' }}>
          {participant.displayName}
          <br />
          {stream && stream.id ? '' : 'You'}
          <br />
          Area Audio: {audio}
        </p>
        {canSpotlight && !isHighlighted && (
          <div
            onClick={async () => {
              highlightParticipant({
                idToken: await getIdToken(),
                key: party.key,
                participant: participant.uid,
              });
            }}
            className={`highlighter ${isHighlighted ? 'active' : ''}`}
            style={
              {
                // transform: `translate(${xForAngle(-90)}px, ${yForAngle(-90)}px)`,
                // ...(isHighlighted ? { opacity: 1 } : {}),
              }
            }>
            <div className='highlighter-button' role='img' aria-label='highlighted'>
              <img alt='settings' src='/icons/spotlight.svg' width={40} height={40}></img>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default connect(AppMapProps)(ParticipantBubble);
